/**
 * Freeze V2 — Embryo Summary Dialog
 *
 * Shows date + site (readonly) and an optional storage location filter
 * before generating the Embryo Summary report via the reports service.
 */
import { useState } from 'react';

import { CheckIcon } from 'lucide-react';

import { useGetFreezeStorageLocationsQuery } from '@/app/services/locations';
import { Button } from '@/components/ui/button';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { useEmbryoSummaryReport } from '@/services/reports';
import { getFertilizationSiteId } from '@/utils/freezeSites';

type StorageLocationOption = { id: string; name: string };

type EmbryoSummaryDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  date: string | null;
  siteId: string | null;
  siteName: string;
};

export function EmbryoSummaryDialog({
  open,
  onOpenChange,
  date,
  siteId,
  siteName,
}: EmbryoSummaryDialogProps) {
  const [generateSummary, { isPending }] = useEmbryoSummaryReport();
  const [selectedLocation, setSelectedLocation] =
    useState<StorageLocationOption | null>(null);

  const { data } = useGetFreezeStorageLocationsQuery(
    getFertilizationSiteId(siteId),
    {
      skip: !siteId,
    },
  );
  const locations = (data?.filter((loc) => loc.name) ??
    []) as StorageLocationOption[];

  const handleConfirm = async () => {
    if (!date || !siteId) return;
    try {
      onOpenChange(false);
      await generateSummary({
        fertilizationSiteId: siteId,
        fertilizationDate: date,
        storageLocationId: selectedLocation?.id,
      });
    } catch {
      onOpenChange(true);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) setSelectedLocation(null);
        onOpenChange(nextOpen);
      }}
    >
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Embryo Summary</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <FieldGroup className="gap-3">
            <Field orientation="horizontal">
              <FieldLabel className="w-1/3">Date</FieldLabel>
              <Input disabled value={date ?? ''} />
            </Field>
            <Field orientation="horizontal">
              <FieldLabel className="w-1/3">Site</FieldLabel>
              <Input disabled value={siteName} />
            </Field>
            <Field orientation="horizontal">
              <FieldLabel className="w-1/3">Storage Loc</FieldLabel>
              <Combobox<StorageLocationOption>
                items={locations}
                value={selectedLocation}
                onValueChange={setSelectedLocation}
                itemToStringValue={(option) => option.id}
                itemToStringLabel={(option) => option.name}
              >
                <ComboboxInput
                  className="w-full"
                  placeholder="All locations"
                  showClear
                />
                <ComboboxContent>
                  <ComboboxEmpty>No locations found</ComboboxEmpty>
                  <ComboboxList>
                    {(option: StorageLocationOption) => (
                      <ComboboxItem key={option.id} value={option}>
                        {option.name}
                      </ComboboxItem>
                    )}
                  </ComboboxList>
                </ComboboxContent>
              </Combobox>
            </Field>
          </FieldGroup>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isPending || !date || !siteId}
          >
            {isPending ? (
              'Generating…'
            ) : (
              <>
                <CheckIcon className="size-4" />
                Generate
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
